import React, { createContext, useContext, useState, useEffect } from 'react';
import empleadosService from '../services/empleadosService';
import { useCompany } from './CompanyContext';

/**
 * ------------------------------------------------------------------
 * EMPLEADO CONTEXT
 * ------------------------------------------------------------------
 * Maneja el estado global de empleados
 * Proporciona funciones CRUD para empleados con caching
 * ------------------------------------------------------------------
 */

const EmpleadoContext = createContext();

export function useEmpleados() {
    const context = useContext(EmpleadoContext);
    if (!context) {
        throw new Error('useEmpleados debe usarse dentro de un EmpleadoProvider');
    }
    return context;
}

export function EmpleadoProvider({ children }) {
    const [empleados, setEmpleados] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { company } = useCompany();

    /**
     * Cargar todos los empleados
     * @param {boolean} force - Forzar recarga desde API
     */
    const fetchEmpleados = async (force = false) => {
        if (!company || !company.rif) {
            console.warn('No hay empresa seleccionada para cargar empleados');
            return;
        }

        // Cache hit
        if (!force && empleados.length > 0) {
            return;
        }

        setLoading(true);
        setError(null);
        try {
            const data = await empleadosService.getAll(company.rif);
            const normalizedData = Array.isArray(data) ? data : (data.data || []);
            setEmpleados(normalizedData);
        } catch (err) {
            console.error('Error fetching empleados:', err);
            setError(err.message || 'Error al cargar empleados');
        } finally {
            setLoading(false);
        }
    };

    /**
     * Crear un nuevo empleado
     */
    const createEmpleado = async (empleadoData) => {
        if (!company || !company.rif) {
            throw new Error('No hay empresa seleccionada');
        }

        setLoading(true);
        setError(null);
        try {
            // Agregar empresa_rif automáticamente
            const dataWithEmpresa = {
                ...empleadoData,
                empresa_rif: company.rif
            };

            const nuevoEmpleado = await empleadosService.create(dataWithEmpresa);
            // Agregar a la lista local
            setEmpleados(prev => [...prev, nuevoEmpleado]);
            return nuevoEmpleado;
        } catch (err) {
            console.error('Error creating empleado:', err);
            setError(err.message || 'Error al crear empleado');
            throw err;
        } finally {
            setLoading(false);
        }
    };

    /**
     * Actualizar un empleado existente
     */
    const updateEmpleado = async (rif, empleadoData) => {
        if (!company || !company.rif) {
            throw new Error('No hay empresa seleccionada');
        }

        setLoading(true);
        setError(null);
        try {
            // Agregar empresa_rif automáticamente
            const dataWithEmpresa = {
                ...empleadoData,
                empresa_rif: company.rif
            };

            const empleadoActualizado = await empleadosService.update(rif, dataWithEmpresa);
            // Actualizar lista local
            setEmpleados(prev =>
                prev.map(item => item.rif === rif ? empleadoActualizado : item)
            );
            return empleadoActualizado;
        } catch (err) {
            console.error(`Error updating empleado ${rif}:`, err);
            setError(err.message || 'Error al actualizar empleado');
            throw err;
        } finally {
            setLoading(false);
        }
    };

    /**
     * Eliminar un empleado
     */
    const deleteEmpleado = async (rif) => {
        setLoading(true);
        setError(null);
        try {
            await empleadosService.delete(rif, company.rif);
            // Remover de lista local
            setEmpleados(prev => prev.filter(item => item.rif !== rif));
        } catch (err) {
            console.error(`Error deleting empleado ${rif}:`, err);
            setError(err.message || 'Error al eliminar empleado');
            throw err;
        } finally {
            setLoading(false);
        }
    };

    // Limpiar caché cuando cambia la empresa
    useEffect(() => {
        setEmpleados([]);
    }, [company?.rif]);

    const value = {
        empleados,
        loading,
        error,
        fetchEmpleados,
        createEmpleado,
        updateEmpleado,
        deleteEmpleado
    };

    return (
        <EmpleadoContext.Provider value={value}>
            {children}
        </EmpleadoContext.Provider>
    );
}
